import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield, Search, RefreshCw, Eye, Clock, FileText,
  Users, Activity, Filter, Download
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ExportButton } from '../components/shared/ExportButton';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  login: 'bg-purple-100 text-purple-700',
  export: 'bg-amber-100 text-amber-700',
  view: 'bg-gray-100 text-gray-600',
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterModule, setFilterModule] = useState('all');
  const [filterAction, setFilterAction] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterModule !== 'all') params.module = filterModule;
      if (filterAction !== 'all') params.action_type = filterAction;
      if (search) params.user_filter = search;

      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/audit-log`, { params,  }),
        axios.get(`${API}/api/audit-log/stats`),
      ]);
      setLogs(logsRes.data.logs || []);
      setStats(statsRes.data);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [filterModule, filterAction, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const modules = [...new Set(stats.by_module?.map(m => m.module) || [])];
  const actions = [...new Set(stats.by_action?.map(a => a.action) || [])];

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="audit-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Audit Log<PageInfoTooltip pageKey="audit-log" /></h1>
          <p className="text-gray-medium text-sm mt-1">Track all system activities and user actions</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            data={logs.map(l => ({ timestamp: l.timestamp, user: l.user_email, action: l.action, module: l.module, description: l.description }))}
            filename="audit_log"
            columns={['timestamp', 'user', 'action', 'module', 'description']}
          />
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Shield} label="Total Events" value={stats.total || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Clock} label="Today" value={stats.today || 0} color="bg-amber-50 text-amber-600" />
        <StatCard icon={Activity} label="Actions" value={actions.length} color="bg-purple-50 text-purple-600" />
        <StatCard icon={FileText} label="Modules" value={modules.length} color="bg-emerald-50 text-emerald-600" />
      </div>

      {/* Stats Cards */}
      {(stats.by_action?.length > 0 || stats.by_module?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {stats.by_action?.length > 0 && (
            <Card className="bg-white border-gray-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">By Action</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {stats.by_action.slice(0, 6).map(a => (
                    <div key={a.action} className="flex items-center justify-between">
                      <Badge className={cn("text-xs capitalize", ACTION_COLORS[a.action] || 'bg-gray-100 text-gray-600')}>{a.action || 'unknown'}</Badge>
                      <span className="text-sm font-medium text-text-primary">{a.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {stats.by_module?.length > 0 && (
            <Card className="bg-white border-gray-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">By Module</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {stats.by_module.slice(0, 6).map(m => (
                    <div key={m.module} className="flex items-center justify-between">
                      <span className="text-sm text-gray-medium capitalize">{m.module || 'unknown'}</span>
                      <span className="text-sm font-medium text-text-primary">{m.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search by user..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="audit-search" />
            </div>
            {modules.length > 0 && (
              <Select value={filterModule} onValueChange={setFilterModule}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Module" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modules</SelectItem>
                  {modules.map(m => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {actions.length > 0 && (
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Action" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actions.map(a => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-medium">
              <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No audit events recorded yet</p>
              <p className="text-xs mt-1">Events will appear here as users interact with the platform</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
              {logs.map((log, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors text-sm" data-testid={`audit-row-${idx}`}>
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="h-3.5 w-3.5 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-text-primary">{log.user_name || 'System'}</span>
                      <Badge className={cn("text-[10px] capitalize", ACTION_COLORS[log.action_type] || 'bg-gray-100 text-gray-600')}>{log.action_type}</Badge>
                      {log.module && <Badge className="text-[10px] bg-blue-50 text-blue-600 capitalize">{log.module}</Badge>}
                    </div>
                    <p className="text-xs text-gray-medium mt-0.5 truncate">{log.details || `${log.action_type} on ${log.module}`}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-medium">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</p>
                    {log.ip_address && <p className="text-[10px] text-gray-400">{log.ip_address}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
